import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { wrap } from 'popmotion'
import { Stack } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faCircle } from '@fortawesome/free-solid-svg-icons'
import { faCircle as faCircleRegular } from '@fortawesome/free-regular-svg-icons'

const sliderVariants = {
  incoming: (direction) => {
    return {
      position: 'absolute',
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }
  },
  active: {
    position: 'relative',
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      position: 'absolute',
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    }
  }
}

const sliderTransition = {
  duration: 1,
  ease: [0.56, 0.03, 0.12, 1.04]
}

const DefaultImageView = ({ image }) => {
  return (
    <>
      <motion.div className="image" style={{
        backgroundImage: `url(${image})`
      }} />
    </>
  )
}

const DefaultPaginationView = ({ images, activeImageIndex, skipToImage }) => {
  const isActive = (index) => {
    return activeImageIndex === index
  }

  return (
    <Stack direction="horizontal" className="carousel-pagination d-flex justify-content-center align-items-center">
      {images.map((image, index) => {
        if (isActive(index)) {
          return (
            <button key={index}>
              <FontAwesomeIcon icon={faCircle} />
            </button>
          )
        }

        return (
          <button key={index} onClick={() => skipToImage(index)}>
            <FontAwesomeIcon icon={faCircleRegular} />
          </button>
        )
      })}
    </Stack>
  )
}

const DefaultNavigationView = ({ swipeToImage }) => {
  return (
    <Stack direction="horizontal" className="carousel-navigation">
      <div className="next" onClick={() => swipeToImage(1)}>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
      <div className="prev" onClick={() => swipeToImage(-1)}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </div>
    </Stack>
  )
}

export const ImageCarousel = ({ 
  images, 
  ImageView = DefaultImageView,
  PaginationView = DefaultPaginationView,
  NavigationView = DefaultNavigationView
}) => {
  const [[imageCount, direction], setImageCount] = useState([0, 0])
  const activeImageIndex = wrap(0, images.length, imageCount)

  const swipeToImage = swipeDirection => {
    setImageCount([imageCount + swipeDirection, swipeDirection])
  }

  const skipToImage = (imageId) => {
    let changeDirection = 0
    if (imageId > activeImageIndex) {
      changeDirection = 1
    } else if (imageId < activeImageIndex) {
      changeDirection = -1
    }
    setImageCount([imageId, changeDirection])
  }

  const dragEndHandler = (dragInfo) => {
    const draggedDistance = dragInfo.offset.x
    const swipeThreshold = 50

    if (draggedDistance > swipeThreshold) {
      swipeToImage(-1)
    } else if (draggedDistance < -swipeThreshold) {
      swipeToImage(1)
    }
  }

  return (
    <>
      <div className="slider-container">
        <div className="slider">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={imageCount}
              custom={direction}
              variants={sliderVariants}
              initial="incoming"
              animate="active"
              exit="exit"
              transition={sliderTransition}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_, dragInfo) => dragEndHandler(dragInfo)}
              className="image">
              <ImageView {...images[activeImageIndex]} />
            </motion.div>
          </AnimatePresence>
        </div>
        <PaginationView 
          images={images} 
          activeImageIndex={activeImageIndex} 
          skipToImage={skipToImage}  
        />
        <NavigationView 
          swipeToImage={swipeToImage}  
        />
      </div>
    </>
  )
}

export default ImageCarousel